import React, { useCallback } from 'react'
import classNames from 'classnames'
import { PauseIcon } from '@/atoms/Icons/PauseIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { useHlsPlayerUI } from './HlsPlayerUIContext'
import { HlsSkipButton } from './HlsSkipButton'

/** Height of the play button circle in pixels. The pause icon is 80% of this size. */
const CENTER_BUTTON_SIZE_PX = 72

interface HlsCenterPlayOverlayProps {
  visible: boolean
}

/**
 * Center overlay with skip backward, play/pause, and skip forward buttons.
 * Clicking the background area (outside buttons) also toggles playback.
 *
 * Play state: white circle with dark play icon.
 * Pause state: bare white pause icon (no background), 80% of the play circle height.
 * Transitions between states with a cross-fade animation.
 */
export const HlsCenterPlayOverlay: React.FC<HlsCenterPlayOverlayProps> = ({ visible }) => {
  const { playing, controller } = useHlsPlayerUI()

  const handleBackgroundClick = useCallback(() => {
    if (!controller) return
    if (playing) {
      controller.pause()
    } else {
      controller.play()
    }
  }, [controller, playing])

  const handlePlayPauseClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!controller) return
      if (playing) {
        controller.pause()
      } else {
        controller.play()
      }
    },
    [controller, playing],
  )

  return (
    <div
      className="absolute inset-0 flex cursor-pointer items-center justify-center"
      onClick={handleBackgroundClick}
      role="presentation"
    >
      {/* Center button cluster: skip back | play/pause | skip forward */}
      <div
        className={classNames(
          'flex items-center gap-12 transition-opacity duration-[400ms]',
          visible ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <HlsSkipButton direction="backward" />

        {/* Play/pause button — both rendered, cross-faded via opacity */}
        <button
          type="button"
          className="relative flex h-[4.5rem] w-[4.5rem] items-center justify-center"
          onClick={handlePlayPauseClick}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {/* Play: white circle with dark icon */}
          <div
            className={classNames(
              'absolute inset-0 flex items-center justify-center rounded-full bg-white transition-opacity duration-[400ms]',
              playing ? 'opacity-0' : 'opacity-100 hover:bg-white/90',
            )}
          >
            <PlayIcon size={44} color="core-gray-950" />
          </div>

          {/* Pause: bare white icon, no background */}
          <div
            className={classNames(
              'absolute inset-0 flex items-center justify-center transition-opacity duration-[400ms]',
              playing ? 'opacity-100' : 'opacity-0',
            )}
          >
            <PauseIcon size={CENTER_BUTTON_SIZE_PX * 0.8} color="core-gray-100" />
          </div>
        </button>

        <HlsSkipButton direction="forward" />
      </div>
    </div>
  )
}
